/**
 * Container and utility block definitions: double / trapped / ender chests,
 * hopper, dropper, dispenser, shulker box, signs, composter levels, cauldron,
 * cake, item frames, flower pots, ladder, vine, chain, lanterns, candles,
 * campfire, lightning rod.
 *
 * Like doors, stairs and the redstone parts, whatever state a block can keep in
 * its id lives there (facing, level, bites, lit...), so saves, the wire and the
 * mesher need nothing extra. What cannot (item stacks, sign text, a frame's
 * item) is a block entity owned by the host (server/actions/utility.ts).
 *
 * blocks.ts calls `defineUtilityBlocks` once while it builds the registry, so
 * this file must not import values from blocks.ts.
 *
 * Face indices (blocks.ts FACE_DIRS): 0 +X, 1 -X, 2 +Y, 3 -Y, 4 +Z, 5 -Z.
 * Facings (chest order): 0 +Z, 1 +X, 2 -Z, 3 -X.
 */
import type { BlockDef, CollisionRect } from '../../blocks';

export const UK = {
  NONE: 0, CHEST: 1, ENDER: 2, HOPPER: 3, DISPENSER: 4, DROPPER: 5, SHULKER: 6, SIGN: 7, WALL_SIGN: 8,
  COMPOSTER: 9, CAULDRON: 10, CAKE: 11, FRAME: 12, POT: 13, LADDER: 14, VINE: 15, CHAIN: 16, LANTERN: 17,
  CANDLE: 18, CAMPFIRE: 19, ROD: 20,
} as const;

const MAX = 8192;
/** Utility kind per block id (UK.NONE for everything else). */
export const U_KIND = new Uint8Array(MAX);
/**
 * First state number. ender: facing; hopper / dispenser / dropper: the face it points at; sign: wood index;
 * composter: level 0..8; cauldron: 0 empty 1 water 2 lava; cake: bites; frame / ladder / vine: the face of the
 * neighbour it hangs on; pot: the potted plant's block id; chain: axis; lantern: 1 soul; candle: count; campfire: lit.
 */
export const U_A = new Int16Array(MAX);
/** Second state number. sign: rotation 0..15; wall sign: facing; cauldron: level 1..3; frame: glow; lantern: hanging; candle: lit. */
export const U_B = new Int16Array(MAX);

const IDS = new Map<number, number>();
const key = (kind: number, a: number, b: number): number => (kind * 4096 + a) * 64 + b;
/** The block id of a utility state, or -1. */
export function uid(kind: number, a = 0, b = 0): number {
  return IDS.get(key(kind, a, b)) ?? -1;
}

// ---- chests ----------------------------------------------------------------------------------

/** Chest state per block id: -1 when the id is not a chest. kind 0 chest, 1 trapped; type 0 single, 1 left, 2 right. */
export const CH_KIND = new Int8Array(MAX).fill(-1);
export const CH_TYPE = new Int8Array(MAX);
export const CH_FACING = new Int8Array(MAX).fill(-1);
export const CH_OPEN = new Uint8Array(MAX);
const CHEST_IDS = new Map<number, number>();
const chestKey = (kind: number, type: number, facing: number, open: boolean | number): number => ((kind * 3 + type) * 4 + facing) * 2 + (open ? 1 : 0);

export function registerChest(id: number, kind: number, type: number, facing: number, open: boolean): void {
  CH_KIND[id] = kind; CH_TYPE[id] = type; CH_FACING[id] = facing; CH_OPEN[id] = open ? 1 : 0;
  U_KIND[id] = UK.CHEST;
  CHEST_IDS.set(chestKey(kind, type, facing, open), id);
}
export function chestId(kind: number, type: number, facing: number, open: boolean | number): number {
  return CHEST_IDS.get(chestKey(kind, type, facing, open)) ?? -1;
}
/** Where a LEFT half's other half is, by facing (dx, dz): clockwise of the facing (vanilla ChestBlock.getConnectedDirection). */
const LEFT_PARTNER: ReadonlyArray<readonly [number, number]> = [[-1, 0], [0, 1], [1, 0], [0, -1]];
/** Offset (dx, dz) from a chest half to its other half, or null for a single chest / a non-chest. */
export function chestPartnerOffset(id: number): readonly [number, number] | null {
  const type = CH_KIND[id] < 0 ? 0 : CH_TYPE[id];
  if (type === 0) return null;
  const [dx, dz] = LEFT_PARTNER[CH_FACING[id]];
  return type === 1 ? [dx, dz] : [-dx, -dz];
}
/** Is `other` the matching half of chest half `id` (same kind and facing, the opposite type)? */
export function chestHalvesMatch(id: number, other: number): boolean {
  return CH_KIND[id] >= 0 && CH_KIND[other] === CH_KIND[id] && CH_FACING[other] === CH_FACING[id]
    && CH_TYPE[id] !== 0 && CH_TYPE[other] === 3 - CH_TYPE[id];
}

// ---- tables filled by defineUtilityBlocks -------------------------------------------------

/** Inventory ids (the canonical state of each block). */
export const UTIL = {
  TRAPPED_CHEST: -1, ENDER_CHEST: -1, HOPPER: -1, DISPENSER: -1, DROPPER: -1, SHULKER: -1, COMPOSTER: -1, CAULDRON: -1, CAKE: -1,
  FRAME: -1, GLOW_FRAME: -1, POT: -1, LADDER: -1, VINE: -1, CHAIN: -1, LANTERN: -1, SOUL_LANTERN: -1, CANDLE: -1,
  CAMPFIRE: -1, ROD: -1,
};
/** Standing sign (rotation 0) per wood index: the inventory id of each sign. */
export const SIGN_IDS: number[] = [];
export const SIGN_WOODS: string[] = [];
/** Every id of each block, canonical first (mining and pick-block map them all to the canonical one). */
export const U_GROUPS: number[][] = [];
/** Target (outline) boxes of blocks without collision. */
export const U_OUTLINES: Array<[number, readonly CollisionRect[]]> = [];
/** Ladders and vines. */
export const U_CLIMBABLE: number[] = [];
/** Facing families (index = facing) placed by the player's yaw like a chest: trapped chest. */
export const U_FACING_FAMILIES: Array<readonly [number, number, number, number]> = [];
/** [potted id, plant id] for every potted plant. */
export const POTTED: Array<[number, number]> = [];

type Six = [number, number, number, number, number, number];
export interface UtilDefApi {
  bid(): number;
  def(id: number, name: string, tiles: Six, opts?: Partial<BlockDef>): void;
  T: Record<string, number>;
  /** Register an id that hangs on the neighbour at `face` / the attached ids of an inventory id by clicked face (blocks.ts ATTACHED, ATTACH_VARIANTS). */
  attached(id: number, face: number): void;
  attachVariants(base: number, byFace: number[]): void;
  /** [name, planks tile] per wood. */
  woods: ReadonlyArray<readonly [string, number]>;
  /** [block id, tile] of every plant a flower pot takes. */
  flora: ReadonlyArray<readonly [number, number]>;
  /** The existing composter block (level 0) and the existing chest ids by facing. */
  composter: number;
  chests: { closed: readonly number[]; open: readonly number[] };
}

const CUTOUT = 1, MODEL = 3;
const all = (t: number): Six => [t, t, t, t, t, t];
const FRONT_FACE = [4, 0, 5, 1];
/** Face index of a facing, and back. */
export const faceOfFacing = (facing: number): number => FRONT_FACE[facing];
export const facingOfFace = (face: number): number => FRONT_FACE.indexOf(face);

/** A thin box against the face `dir`, `w` x `h` wide and `d` deep, in 1/16. */
export function attachedBox(dir: number, w: number, h: number, d: number): CollisionRect {
  const a = (16 - w) / 2, b = (16 - h) / 2;
  switch (dir) {
    case 0: return [16 - d, b, a, 16, 16 - b, 16 - a];
    case 1: return [0, b, a, d, 16 - b, 16 - a];
    case 2: return [a, 16 - d, b, 16 - a, 16, 16 - b];
    case 3: return [a, 0, b, 16 - a, d, 16 - b];
    case 4: return [a, b, 16 - d, 16 - a, 16 - b, 16];
    default: return [a, b, 0, 16 - a, 16 - b, d];
  }
}

/** The hollow tub of a composter / cauldron: a floor and four walls. */
export const TUB: readonly CollisionRect[] = [
  [0, 0, 0, 16, 2, 16], [0, 2, 0, 2, 16, 16], [14, 2, 0, 16, 16, 16], [2, 2, 0, 14, 16, 2], [2, 2, 14, 14, 16, 16],
];
const CHEST_BOX: CollisionRect = [1, 0, 1, 15, 14, 15];
/** A chest half's box: the single box stretched to the edge its other half is on. */
export function chestBox(type: number, facing: number): CollisionRect {
  if (type === 0) return CHEST_BOX;
  const [lx, lz] = LEFT_PARTNER[facing];
  const dx = type === 1 ? lx : -lx, dz = type === 1 ? lz : -lz;
  return [dx < 0 ? 0 : 1, 0, dz < 0 ? 0 : 1, dx > 0 ? 16 : 15, 14, dz > 0 ? 16 : 15];
}

export function defineUtilityBlocks(api: UtilDefApi): void {
  const { T } = api;
  let group: number[] = [];
  const start = (): void => { group = []; U_GROUPS.push(group); };
  /** Define one state; the first of a group is the placeable inventory id. */
  const reg = (name: string, tiles: Six, opts: Partial<BlockDef>, kind: number, a = 0, b = 0, outline?: readonly CollisionRect[]): number => {
    const id = api.bid();
    if (id >= MAX) throw new Error('utility: block ids exhausted');
    api.def(id, name, tiles, { ...opts, placeable: group.length === 0 && opts.placeable !== false });
    U_KIND[id] = kind; U_A[id] = a; U_B[id] = b;
    IDS.set(key(kind, a, b), id);
    group.push(id);
    if (outline) U_OUTLINES.push([id, outline]);
    return id;
  };
  const model = { solid: true, opaque: false, pass: CUTOUT, shape: MODEL } as const;
  const thin = { solid: false, opaque: false, pass: CUTOUT, shape: MODEL, needsSupport: true, rigid: true } as const;

  // ---- chests: the eight existing ids are the single plain chest ----
  for (let f = 0; f < 4; f++) { registerChest(api.chests.closed[f], 0, 0, f, false); registerChest(api.chests.open[f], 0, 0, f, true); }
  const chest = (name: string, kind: number, type: number, facing: number, open: boolean, placeable: boolean): number => {
    const id = api.bid();
    api.def(id, name, all(T.PLANKS), { ...model, placeable, collision: [chestBox(type, facing)] });
    registerChest(id, kind, type, facing, open);
    return id;
  };
  {
    // plain double halves mine to the plain chest (their group's canonical id)
    const plain: number[] = [api.chests.closed[0]];
    U_GROUPS.push(plain);
    for (let f = 1; f < 4; f++) plain.push(api.chests.closed[f]);
    for (let f = 0; f < 4; f++) plain.push(api.chests.open[f]);
    for (const type of [1, 2]) for (let f = 0; f < 4; f++) for (const open of [false, true]) plain.push(chest('Chest', 0, type, f, open, false));
    const trapped: number[] = [];
    U_GROUPS.push(trapped);
    const fam: number[] = [];
    for (let f = 0; f < 4; f++) { fam[f] = chest('Trapped Chest', 1, 0, f, false, f === 0); trapped.push(fam[f]); }
    for (let f = 0; f < 4; f++) trapped.push(chest('Trapped Chest', 1, 0, f, true, false));
    for (const type of [1, 2]) for (let f = 0; f < 4; f++) for (const open of [false, true]) trapped.push(chest('Trapped Chest', 1, type, f, open, false));
    UTIL.TRAPPED_CHEST = fam[0];
    U_FACING_FAMILIES.push([fam[0], fam[1], fam[2], fam[3]]);
  }

  // ---- ender chest ----
  start();
  for (let f = 0; f < 4; f++) reg('Ender Chest', all(T.OBSIDIAN), { ...model, light: 7, collision: [CHEST_BOX] }, UK.ENDER, f);
  UTIL.ENDER_CHEST = group[0];

  // ---- hopper: points down or at one of the four sides ----
  start();
  for (const face of [3, 0, 1, 4, 5]) {
    reg('Hopper', all(T.HOPPER_OUTSIDE), { ...model, collision: [[0, 10, 0, 16, 16, 16], [4, 4, 4, 12, 10, 12]] }, UK.HOPPER, face);
  }
  UTIL.HOPPER = group[0];

  // ---- dispenser and dropper: full cubes, one id per face the mouth points out of ----
  for (const [name, kind, front, frontV] of [
    ['Dispenser', UK.DISPENSER, T.DISPENSER_FRONT, T.DISPENSER_FRONT_V], ['Dropper', UK.DROPPER, T.DROPPER_FRONT, T.DROPPER_FRONT_V],
  ] as const) {
    start();
    // the inventory id faces +Z like the furnace's
    for (const face of [4, 0, 5, 1, 2, 3]) {
      const tiles: Six = [T.FURNACE_SIDE, T.FURNACE_SIDE, T.FURNACE_TOP, T.FURNACE_TOP, T.FURNACE_SIDE, T.FURNACE_SIDE];
      if (face === 2 || face === 3) { tiles[0] = tiles[1] = tiles[4] = tiles[5] = T.FURNACE_TOP; tiles[face] = frontV; } else tiles[face] = front;
      reg(name, tiles, {}, kind, face);
    }
    if (kind === UK.DISPENSER) UTIL.DISPENSER = group[0]; else UTIL.DROPPER = group[0];
  }

  // ---- shulker box ----
  start();
  UTIL.SHULKER = reg('Shulker Box', all(T.SHULKER_SIDE), model, UK.SHULKER);

  // ---- signs: 16 standing rotations and 4 wall facings per wood ----
  api.woods.forEach(([wood, tile], wi) => {
    start();
    SIGN_WOODS[wi] = wood;
    for (let r = 0; r < 16; r++) reg(`${wood} Sign`, all(tile), thin, UK.SIGN, wi, r, [[4, 0, 4, 12, 16, 12]]);
    SIGN_IDS[wi] = group[0];
    const byFace: number[] = [];
    for (let f = 0; f < 4; f++) {
      // the board hangs on the block behind it: facing f looks away from the neighbour on the opposite face
      const behind = FRONT_FACE[f] ^ 1;
      const id = reg(`${wood} Sign`, all(tile), thin, UK.WALL_SIGN, wi, f, [attachedBox(behind, 16, 8, 2)]);
      api.attached(id, behind);
      byFace[behind] = id;
    }
    api.attachVariants(group[0], byFace);
  });

  // ---- composter: level 0 is the block that was already there ----
  U_KIND[api.composter] = UK.COMPOSTER;
  IDS.set(key(UK.COMPOSTER, 0, 0), api.composter);
  UTIL.COMPOSTER = api.composter;
  group = [api.composter];
  U_GROUPS.push(group);
  {
    const tiles: Six = [T.COMPOSTER_SIDE, T.COMPOSTER_SIDE, T.COMPOSTER_TOP, T.COMPOSTER_BOTTOM, T.COMPOSTER_SIDE, T.COMPOSTER_SIDE];
    api.def(api.composter, 'Composter', tiles, { ...model, collision: TUB }); // a hollow tub, not the plain cube it used to be
    for (let level = 1; level <= 8; level++) reg('Composter', tiles, { ...model, collision: TUB, placeable: false }, UK.COMPOSTER, level);
  }

  // ---- cauldron: empty, water 1..3, lava ----
  start();
  {
    const tiles: Six = [T.CAULDRON_SIDE, T.CAULDRON_SIDE, T.CAULDRON_TOP, T.CAULDRON_BOTTOM, T.CAULDRON_SIDE, T.CAULDRON_SIDE];
    UTIL.CAULDRON = reg('Cauldron', tiles, { ...model, collision: TUB }, UK.CAULDRON, 0, 0);
    for (let level = 1; level <= 3; level++) reg('Cauldron', tiles, { ...model, collision: TUB }, UK.CAULDRON, 1, level);
    reg('Cauldron', tiles, { ...model, collision: TUB, light: 15 }, UK.CAULDRON, 2, 3);
  }

  // ---- cake: 7 slices ----
  start();
  for (let bites = 0; bites < 7; bites++) {
    reg('Cake', [T.CAKE_SIDE, T.CAKE_SIDE, T.CAKE_TOP, T.CAKE_BOTTOM, T.CAKE_SIDE, T.CAKE_SIDE],
      { ...model, needsSupport: true, collision: [[1 + bites * 2, 0, 1, 15, 8, 15]] }, UK.CAKE, bites);
  }
  UTIL.CAKE = group[0];

  // ---- item frames: one id per face of the block they hang on ----
  for (const glow of [0, 1]) {
    start();
    const byFace: number[] = [];
    // the inventory id hangs on the -Z neighbour
    for (const face of [5, 4, 0, 1, 2, 3]) {
      const id = reg(glow ? 'Glow Item Frame' : 'Item Frame', all(glow ? T.GLOW_ITEM_FRAME : T.ITEM_FRAME), { ...thin, light: 0 },
        UK.FRAME, face, glow, [attachedBox(face, 12, 12, 1)]);
      api.attached(id, face);
      byFace[face] = id;
    }
    api.attachVariants(group[0], byFace);
    if (glow) UTIL.GLOW_FRAME = group[0]; else UTIL.FRAME = group[0];
  }

  // ---- flower pot, empty and with each plant ----
  start();
  {
    const pot = { solid: false, opaque: false, pass: CUTOUT, shape: MODEL, collision: [[5, 0, 5, 11, 6, 11]] as CollisionRect[] } as const;
    UTIL.POT = reg('Flower Pot', all(T.FLOWER_POT), pot, UK.POT, 0);
    for (const [plant, tile] of api.flora) {
      const id = reg('Flower Pot', [T.FLOWER_POT, T.FLOWER_POT, tile, T.FLOWER_POT, T.FLOWER_POT, T.FLOWER_POT], pot, UK.POT, plant);
      POTTED.push([id, plant]);
    }
  }

  // ---- ladder and vine: one id per wall ----
  for (const [name, kind, tile] of [['Ladder', UK.LADDER, T.LADDER], ['Vine', UK.VINE, T.VINE]] as const) {
    start();
    const byFace: number[] = [];
    for (const face of [5, 4, 0, 1]) {
      const box = attachedBox(face, 16, 16, kind === UK.LADDER ? 3 : 1);
      const id = reg(name, all(tile), kind === UK.LADDER ? { ...thin, collision: [box] } : thin, kind, face, 0, kind === UK.LADDER ? undefined : [box]);
      api.attached(id, face);
      byFace[face] = id;
      U_CLIMBABLE.push(id);
    }
    api.attachVariants(group[0], byFace);
    if (kind === UK.LADDER) UTIL.LADDER = group[0]; else UTIL.VINE = group[0];
  }

  // ---- chain: one id per axis (the inventory id is vertical) ----
  start();
  for (const axis of [1, 0, 2]) {
    const box: CollisionRect = axis === 1 ? [6.5, 0, 6.5, 9.5, 16, 9.5] : axis === 0 ? [0, 6.5, 6.5, 16, 9.5, 9.5] : [6.5, 6.5, 0, 9.5, 9.5, 16];
    reg('Chain', all(T.CHAIN), { solid: false, opaque: false, pass: CUTOUT, shape: MODEL, collision: [box] }, UK.CHAIN, axis);
  }
  UTIL.CHAIN = group[0];

  // ---- lanterns: standing, or hanging under a block ----
  for (const soul of [0, 1]) {
    start();
    const name = soul ? 'Soul Lantern' : 'Lantern', tile = soul ? T.SOUL_LANTERN : T.LANTERN, light = soul ? 10 : 15;
    const base = reg(name, all(tile), { ...thin, light, collision: [[5, 0, 5, 11, 9, 11]] }, UK.LANTERN, soul, 0);
    const hang = reg(name, all(tile), { ...thin, light, collision: [[5, 1, 5, 11, 10, 11]] }, UK.LANTERN, soul, 1);
    api.attached(hang, 2);
    const byFace: number[] = [];
    byFace[2] = hang;
    api.attachVariants(base, byFace);
    if (soul) UTIL.SOUL_LANTERN = base; else UTIL.LANTERN = base;
  }

  // ---- candles: 1..4 on a floor, unlit and lit ----
  start();
  for (const lit of [0, 1]) for (let n = 1; n <= 4; n++) {
    const w = n === 1 ? 2 : n === 2 ? 5 : 6;
    reg('Candle', all(lit ? T.CANDLE_LIT : T.CANDLE), { ...thin, light: lit ? 3 * n : 0 }, UK.CANDLE, n, lit,
      [[8 - w / 2 - 1, 0, 8 - w / 2 - 1, 8 + w / 2 + 1, 6, 8 + w / 2 + 1]]);
  }
  UTIL.CANDLE = group[0];

  // ---- campfire: lit (the inventory id) and out ----
  start();
  UTIL.CAMPFIRE = reg('Campfire', all(T.CAMPFIRE_LOG_LIT), { ...model, solid: false, light: 15, collision: [[0, 0, 0, 16, 7, 16]] }, UK.CAMPFIRE, 1);
  reg('Campfire', all(T.CAMPFIRE_LOG), { ...model, solid: false, collision: [[0, 0, 0, 16, 7, 16]] }, UK.CAMPFIRE, 0);

  // ---- lightning rod ----
  start();
  UTIL.ROD = reg('Lightning Rod', all(T.LIGHTNING_ROD), { solid: false, opaque: false, pass: CUTOUT, shape: MODEL, collision: [[6, 0, 6, 10, 16, 10]] }, UK.ROD);
}

/** The inventory id every state of a utility block mines / picks to (the id itself when it is not one). */
const CANON_OF = new Int16Array(MAX).fill(-1);
export function utilityCanonical(id: number): number {
  if (CANON_OF[0] === -1) {
    CANON_OF[0] = 0;
    for (const g of U_GROUPS) for (const m of g) CANON_OF[m] = g[0];
  }
  const c = CANON_OF[id];
  return c > 0 ? c : id;
}

/** Blocks that keep item stacks on the host: slot count, or 0. (The ender chest's stacks belong to the player.) */
export function boxSlots(id: number): number {
  switch (U_KIND[id]) {
    case UK.HOPPER: return 5;
    case UK.DISPENSER: case UK.DROPPER: return 9;
    case UK.SHULKER: return 27;
    default: return 0;
  }
}
export function boxTitle(id: number): string {
  switch (U_KIND[id]) {
    case UK.HOPPER: return 'Hopper';
    case UK.DISPENSER: return 'Dispenser';
    case UK.DROPPER: return 'Dropper';
    case UK.SHULKER: return 'Shulker Box';
    case UK.CHEST: return CH_KIND[id] === 1 ? 'Trapped Chest' : 'Chest';
    default: return '';
  }
}
